import { acquirePlaybackLock } from '../components/ExternalLink/playerState';
import { prepareMediaPermissions } from './devicePermissions';

export const RECORDING_LIMITS = { audio: { seconds: 300, count: 10 }, video: { seconds: 60, count: 5 } };
const mimeTypes = {
  audio: ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm'],
  video: ['video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'],
};
export function recordingSupported() {
  return Boolean(window.isSecureContext !== false && navigator.mediaDevices?.getUserMedia
    && window.MediaRecorder);
}
export function preferredMime(kind) {
  return mimeTypes[kind].find(type => MediaRecorder.isTypeSupported?.(type)) || '';
}
const stopTracks = stream => stream?.getTracks().forEach(track => track.stop());

// Permission promises cannot be aborted. Generations make every late stream
// close its tracks instead of taking ownership after cancel or camera switch.
export class RecordingSession {
  constructor({ kind, onState, onComplete, onError, onStream }) {
    Object.assign(this, { kind, onState, onComplete, onError, onStream });
    this.generation = 0;
    this.disposed = false;
    this.chunks = [];
    this.onVisibility = () => { if (document.visibilityState === 'hidden') this.stop(); };
    this.onPageHide = () => this.stop();
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('pagehide', this.onPageHide);
  }

  async prepare({ facingMode = 'user', silent = false } = {}) {
    if (this.disposed || this.stopping || ['recording', 'paused'].includes(this.recorder?.state)) return;
    if (!this.releasePlayback) this.releasePlayback = acquirePlaybackLock();
    const generation = ++this.generation;
    stopTracks(this.stream);
    this.stream = null;
    this.onStream?.(null);
    this.onState?.('permission');
    try {
      if (!recordingSupported()) throw new Error('MEDIA_RECORDING_UNSUPPORTED');
      const permission = prepareMediaPermissions({ kind: this.kind, silent,
        isCurrent: () => !this.disposed && generation === this.generation });
      if (permission && !await permission) return;
      if (this.disposed || generation !== this.generation) return;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: !silent,
        video: this.kind === 'video' ? { facingMode: { ideal: facingMode },
          aspectRatio: { ideal: 3 / 4 }, width: { ideal: 720 }, height: { ideal: 960 },
          resizeMode: { ideal: 'crop-and-scale' }, frameRate: { ideal: 30 } } : false });
      if (this.disposed || generation !== this.generation) { stopTracks(stream); return; }
      this.stream = stream;
      stream.getTracks().forEach(track => track.addEventListener?.('ended', () => {
        if (this.disposed || this.stopping || generation !== this.generation || this.stream !== stream) return;
        const recording = ['recording', 'paused'].includes(this.recorder?.state);
        this.stop();
        if (!recording) this.onError?.(new DOMException('Capture ended', 'AbortError'));
      }, { once: true }));
      this.onStream?.(stream);
      this.onState?.('prepared');
    } catch (error) {
      if (!this.disposed && generation === this.generation) {
        this.unlockPlayback();
        this.onState?.('error');
        this.onError?.(error);
      }
    }
  }

  start() {
    if (this.disposed || this.stopping || !this.stream || ['recording', 'paused'].includes(this.recorder?.state)) return;
    if (this.stream.getTracks().some(track => track.readyState === 'ended')) {
      this.stop();
      this.onError?.(new DOMException('Capture ended', 'AbortError'));
      return;
    }
    try {
      const mimeType = preferredMime(this.kind);
      this.chunks = [];
      this.finished = false;
      this.cancelled = false;
      // Preserve detail before the worker's one delivery encode. Audio keeps
      // the browser default; the video target is ~38 MB per minute (a hint,
      // not a size guarantee; upload quotas remain enforced by the API).
      this.recorder = new MediaRecorder(this.stream, {
        ...(mimeType ? { mimeType } : {}),
        ...(this.kind === 'video' ? { videoBitsPerSecond: 5000000 } : {}),
      });
      this.recorder.ondataavailable = event => { if (event.data?.size) this.chunks.push(event.data); };
      this.recorder.onerror = event => { this.onError?.(event.error || new Error('MEDIA_RECORDING_FAILED')); this.stop(); };
      this.recorder.onstop = () => {
        this.clearTimers();
        stopTracks(this.stream);
        this.stream = null;
        this.onStream?.(null);
        this.unlockPlayback();
        if (this.finished) return;
        this.finished = true;
        this.stopping = false;
        // onstop follows the final dataavailable. Never build a Blob in stop().
        const blob = new Blob(this.chunks, { type: this.recorder.mimeType || this.chunks[0]?.type || mimeType });
        this.chunks = [];
        if (this.cancelled) return;
        if (!blob.size) { this.onState?.('error'); this.onError?.(new Error('MEDIA_RECORDING_EMPTY')); return; }
        this.onState?.('preview');
        this.onComplete?.({ blob, kind: this.kind,
          duration_ms: Math.max(1, Math.min(this.elapsed || Date.now() - this.startedAt,
            RECORDING_LIMITS[this.kind].seconds * 1000)) });
      };
      this.startedAt = Date.now();
      this.elapsed = 0;
      this.recorder.start(1000);
      this.onState?.('recording');
      const maximum = RECORDING_LIMITS[this.kind].seconds * 1000;
      this.timer = setTimeout(() => this.stop(), maximum);
      this.ticker = setInterval(() => {
        this.onState?.('recording', Math.min(maximum, Date.now() - this.startedAt));
        if (Date.now() - this.startedAt >= maximum) this.stop();
      }, 250);
    } catch (error) {
      this.clearTimers();
      stopTracks(this.stream);
      this.onStream?.(null);
      this.unlockPlayback();
      this.onState?.('error');
      this.onError?.(error);
    }
  }

  stop() {
    // Native stop() becomes inactive before its final dataavailable/onstop.
    // Keep this interval locked even if pagehide and visibilitychange both fire.
    if (this.stopping) return;
    if (this.recorder?.state === 'recording' || this.recorder?.state === 'paused') {
      this.stopping = true;
      this.elapsed = Date.now() - this.startedAt;
      this.onState?.('stopping');
      this.clearTimers();
      this.recorder.stop();
      stopTracks(this.stream);
    } else if (!this.recorder || this.recorder.state === 'inactive') {
      ++this.generation;
      stopTracks(this.stream);
      this.stream = null;
      this.onStream?.(null);
      this.unlockPlayback();
      if (!this.finished) this.onState?.('interrupted');
    }
  }

  clearTimers() { clearTimeout(this.timer); clearInterval(this.ticker); }
  unlockPlayback() { this.releasePlayback?.(); this.releasePlayback = null; }
  dispose({ discard = false } = {}) {
    this.cancelled = discard;
    this.disposed = true;
    ++this.generation;
    this.stop();
    this.clearTimers();
    stopTracks(this.stream);
    this.unlockPlayback();
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('pagehide', this.onPageHide);
  }
}
